// Step 2 — Connect GitHub.
//
// v1 uses a pasted token (see lib/github.ts for why not OAuth). We give
// the creator a one-click deep link to GitHub's token page with the
// exact scopes pre-checked, then a paste field. On connect we validate
// the token (and its scopes) before advancing.

import { useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useOnboardingStore } from "../../store/onboardingStore";
import { newTokenUrl } from "../../lib/github";

export function StepGithub() {
  const user = useAuthStore((s) => s.user)!;
  const busy = useOnboardingStore((s) => s.busy);
  const error = useOnboardingStore((s) => s.error);
  const connectGithub = useOnboardingStore((s) => s.connectGithub);
  const clearError = useOnboardingStore((s) => s.clearError);
  const [token, setToken] = useState("");

  const onConnect = async () => {
    if (!token.trim() || busy) return;
    const ok = await connectGithub(user.sub, token);
    if (ok) setToken("");
  };

  return (
    <div className="step">
      <div className="step-kicker">Step 2 of 7</div>
      <h1 className="step-title">Connect GitHub</h1>
      <p className="step-why">
        Your writing lives in <em>your</em> GitHub account — two repos, one private for drafts
        and one public for what you publish. BKA needs a token to create and write to them.
        It stays in your browser; we never see it.
      </p>

      <ol className="gh-howto">
        <li>
          <a href={newTokenUrl()} target="_blank" rel="noreferrer" className="step-btn step-btn-ghost gh-mintbtn">
            Create a token on GitHub ↗
          </a>
          <div className="step-hint">
            Opens GitHub with the <code>repo</code> + <code>workflow</code> scopes pre-checked.
            Set an expiry you're comfortable with, scroll down, and click <strong>Generate token</strong>.
          </div>
        </li>
        <li>
          <div className="step-field">
            <label className="step-label" htmlFor="gh-token">Paste your token</label>
            <input
              id="gh-token"
              className="step-input"
              type="password"
              autoComplete="off"
              placeholder="ghp_…"
              value={token}
              onChange={(e) => { setToken(e.target.value); if (error) clearError(); }}
              onKeyDown={(e) => { if (e.key === "Enter") void onConnect(); }}
            />
          </div>
        </li>
      </ol>

      <div className="step-actions">
        <button className="step-btn" disabled={!token.trim() || busy} onClick={() => void onConnect()}>
          {busy ? "Connecting…" : "Connect GitHub"}
        </button>
      </div>

      {error && (
        <div className="step-error">
          <div className="step-error-title">couldn't connect</div>
          {error}
        </div>
      )}

      <style>{`
        .gh-howto { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-4); }
        .gh-howto > li { display: flex; flex-direction: column; gap: var(--space-2); }
        .gh-mintbtn { display: inline-block; width: fit-content; text-decoration: none; }
      `}</style>
    </div>
  );
}
